import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { RoutePlaceholder } from "@portal/features/RoutePlaceholder";
import { useSession } from "@core/session/SessionProvider";
import { ChangePasswordScreen } from "./ChangePasswordScreen";
import { PortalShell } from "./PortalShell";
import { RequirePasswordChange } from "./RequirePasswordChange";
import { NAV, type NavEntry } from "./nav";
import { CatalogsScreen } from "@portal/features/catalogs";
import { ProfilesScreen } from "@portal/features/profiles";
import { UsersScreen } from "@portal/features/users";
import { AccessLogScreen } from "@portal/features/access-log";
import { HttpRequestLogScreen } from "@portal/features/http-request-log";
import { MembersScreen } from "@portal/features/members";
import { CircleManagementScreen } from "@portal/features/referrals";
import {
  GrandPrizeConfigScreen,
  GrandPrizeProgressScreen,
} from "@portal/features/grand-prize";
import { PointsEngineScreen } from "@portal/features/points-engine";
import { RewardsScreen } from "@portal/features/rewards";
import { BenefitsScreen } from "@portal/features/benefits";
import { OffersScreen } from "@portal/features/offers";
import { WelcomeContentScreen } from "@portal/features/welcome-content";
import { HomeContentScreen } from "@portal/features/home-content";
import { SchedulingScreen } from "@portal/features/scheduling";
import { RequestsScreen } from "@portal/features/requests";
import { MessageTemplatesScreen } from "@portal/features/message-templates";
import { RedemptionsScreen } from "@portal/features/redemptions";
import { NotificationsScreen } from "@portal/features/notifications";

// TABS-B (human-resolution-tabs-pattern.json): una entrada de NAV que
// declara `children` nunca debe caer en un RoutePlaceholder — redirige a su
// primer hijo visible, con el mismo criterio de visibilidad que usa
// Sidebar.tsx (`hasPermission(entry.permissionModule, "canView")`), acá
// evaluado sobre cada hijo. Genérico a propósito: cualquier entrada futura
// que agregue `children` queda cubierta sin tocar este archivo de nuevo.
// Si ningún hijo es visible (perfil sin permiso sobre ninguno) se cae al
// primer hijo igual — la autorización real la resuelve el servidor
// (ADR-011), y esto evita dejar la ruta padre sin destino.
export function FirstVisibleChildRedirect({ entry }: { entry: NavEntry }) {
  const { hasPermission } = useSession();
  const children = entry.children ?? [];
  const target =
    children.find((child) =>
      hasPermission(child.permissionModule, "canView"),
    ) ?? children[0];
  if (!target) {
    return (
      <RoutePlaceholder
        title={entry.label}
        description={`Módulo: ${entry.permissionModule}`}
      />
    );
  }
  // Absoluto (con "/" inicial), no relativo: `target` es un hermano de esta
  // ruta en el árbol (no un descendiente), así que una resolución relativa
  // depende de la posición de este componente en la navegación y puede no
  // apuntar a la ruta real (verificado con createMemoryRouter en
  // routes.test.tsx).
  return <Navigate to={target.path} replace />;
}

// Placeholders de ruta para las 12 entradas de navegación (SPEC-201 §5), sin
// contenido funcional: cada módulo lo reemplaza en su propia SPEC.
function placeholderRoutes(): RouteObject[] {
  const routes: RouteObject[] = [];
  for (const entry of NAV) {
    if (entry.path === "/welcome-screen") {
      routes.push({
        path: "welcome-screen",
        element: <WelcomeContentScreen />,
      });
      continue;
    }
    if (entry.path === "/home-screen") {
      routes.push({ path: "home-screen", element: <HomeContentScreen /> });
      continue;
    }
    if (entry.path === "/scheduling") {
      routes.push({ path: "scheduling", element: <SchedulingScreen /> });
      continue;
    }
    if (entry.path === "/members") {
      routes.push({ path: "members", element: <MembersScreen /> });
      continue;
    }
    if (entry.path === "/member-circle") {
      routes.push({
        path: "member-circle",
        element: <CircleManagementScreen />,
      });
      continue;
    }
    if (entry.path === "/catalogs") {
      routes.push({ path: "catalogs", element: <CatalogsScreen /> });
      continue;
    }
    if (entry.path === "/points-engine") {
      routes.push({ path: "points-engine", element: <PointsEngineScreen /> });
      continue;
    }
    if (entry.path === "/rewards") {
      routes.push({ path: "rewards", element: <RewardsScreen /> });
      continue;
    }
    if (entry.path === "/requests") {
      routes.push({ path: "requests", element: <RequestsScreen /> });
      continue;
    }
    if (entry.path === "/message-templates") {
      routes.push({
        path: "message-templates",
        element: <MessageTemplatesScreen />,
      });
      continue;
    }
    if (entry.path === "/notifications") {
      routes.push({ path: "notifications", element: <NotificationsScreen /> });
      continue;
    }
    if (entry.path === "/redemptions") {
      routes.push({ path: "redemptions", element: <RedemptionsScreen /> });
      continue;
    }
    routes.push({
      path: entry.path.replace(/^\//, ""),
      element: entry.children?.length ? (
        <FirstVisibleChildRedirect entry={entry} />
      ) : (
        <RoutePlaceholder
          title={entry.label}
          description={`Módulo: ${entry.permissionModule}`}
        />
      ),
    });
    if (entry.children) {
      for (const child of entry.children) {
        if (child.path === "/security/profiles") {
          routes.push({
            path: child.path.replace(/^\//, ""),
            element: <ProfilesScreen />,
          });
          continue;
        }
        if (child.path === "/security/users") {
          routes.push({
            path: child.path.replace(/^\//, ""),
            element: <UsersScreen />,
          });
          continue;
        }
        if (child.path === "/security/access-log") {
          routes.push({
            path: child.path.replace(/^\//, ""),
            element: <AccessLogScreen />,
          });
          continue;
        }
        if (child.path === "/security/http-request-log") {
          routes.push({
            path: child.path.replace(/^\//, ""),
            element: <HttpRequestLogScreen />,
          });
          continue;
        }
        if (child.path === "/prizes/configuration") {
          routes.push({
            path: child.path.replace(/^\//, ""),
            element: <GrandPrizeConfigScreen />,
          });
          continue;
        }
        if (child.path === "/prizes/progress") {
          routes.push({
            path: child.path.replace(/^\//, ""),
            element: <GrandPrizeProgressScreen />,
          });
          continue;
        }
        routes.push({
          path: child.path.replace(/^\//, ""),
          element: (
            <RoutePlaceholder
              title={child.label}
              description={`Módulo: ${child.permissionModule}`}
            />
          ),
        });
      }
    }
  }
  return routes;
}

export const portalRoutes: RouteObject[] = [
  {
    element: <RequirePasswordChange />,
    children: [
      {
        path: "change-password",
        element: <ChangePasswordScreen />,
      },
      {
        element: <PortalShell />,
        children: [
          {
            index: true,
            element: (
              <Navigate to={NAV[0]?.path.replace(/^\//, "") ?? ""} replace />
            ),
          },
          // SPEC-211: "Beneficios Star Circle" no es una entrada propia de
          // NAV (SPEC-201 §2 la agrupa junto a "Recompensas" bajo una sola
          // entrada de menú, /rewards) — se llega acá por el chip de
          // PrizesTabsRow (PortalPrimitives.tsx), igual que "Beneficios Star
          // Circle" ya navegaba a /rewards antes de que esta SPEC existiera.
          // Por eso esta ruta se agrega directo, no vía `placeholderRoutes()`
          // (que solo itera `NAV`).
          { path: "benefits", element: <BenefitsScreen /> },
          // NAV-01 (human-resolution-nav-01-offers-tab.json), mismo criterio
          // que /benefits arriba: "Ofertas" no es una entrada propia de NAV
          // — se llega por el cuarto chip de PrizesTabsRow
          // (PortalPrimitives.tsx), por eso esta ruta también se agrega
          // directo, no vía `placeholderRoutes()`.
          { path: "offers", element: <OffersScreen /> },
          ...placeholderRoutes(),
        ],
      },
    ],
  },
];
